import React, { createContext, useContext, useMemo, useReducer } from 'react'

import { FinancialWorkspaceValueObjectsPeriodEnum } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { LoadingWithSpinner } from 'modules/finance/components/FinancialWorkspace/components/LoadingWithSpinner'
import {
  FinancialWorkspaceAvailabilities,
  useFinancialWorkspaceAvailabilities,
} from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceAvailabilities'
import { useSupportedCurrencies } from 'modules/finance/components/FinancialWorkspace/hooks/useSupportedCurrencies'
import { Range } from 'modules/finance/components/FinancialWorkspace/types/range'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { TableDataVariables } from 'modules/finance/components/FinancialWorkspace/types/table'

interface Props {
  children: React.ReactNode
  defaultGroupBy: InsightTableGroupBy
  insightTableType: InsightTableType
}

interface IValue {
  state: State
  hasComparisonDate: boolean
  supportedCurrencies: string[]
  dispatch: React.Dispatch<Action>
  formattedState: TableDataVariables
  insightTableType: InsightTableType
  availabilities: FinancialWorkspaceAvailabilities
}

export interface FilterIdsKeys extends Record<string, unknown> {
  selectedRange: Range
  selectedLegalEntityIds?: string[]
  selectedEmployeeIds?: string[]
  locationIds?: string[]
  costCenterIds?: string[]
  conceptIds?: string[]
  policyIds?: string[]
}

export interface State extends FilterIdsKeys {
  selectedRange: Range
  compareToRange?: Range
  insightTableType: InsightTableType
  period: FinancialWorkspaceValueObjectsPeriodEnum
}

export type Action =
  | { type: 'SET_RANGE'; payload: Range }
  | { type: 'SET_EMPLOYEES'; payload?: string[] }
  | { type: 'SET_LOCATIONS'; payload?: string[] }
  | { type: 'SET_POLICY_IDS'; payload?: string[] }
  | { type: 'SET_CONCEPT_IDS'; payload?: string[] }
  | { type: 'SET_COST_CENTERS'; payload?: string[] }
  | { type: 'SET_COMPARE_TO_RANGE'; payload?: Range }
  | { type: 'SET_LEGAL_ENTITIES'; payload?: string[] }
  | { type: 'SET_INSIGHT_TABLE_TYPE'; payload: InsightTableType }
  | { type: 'SET_PERIOD'; payload: FinancialWorkspaceValueObjectsPeriodEnum }

const initialState: State = {
  insightTableType: InsightTableType.Payroll,
  selectedRange: [
    moment().startOf('year').clone().toDate(),
    moment().endOf('year').clone().toDate(),
  ],
  selectedLegalEntityIds: undefined,
  selectedEmployeeIds: undefined,
  period: 'monthly',
  locationIds: undefined,
  costCenterIds: undefined,
  conceptIds: undefined,
  policyIds: undefined,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_RANGE':
      return { ...state, selectedRange: action.payload }
    case 'SET_COMPARE_TO_RANGE':
      return { ...state, compareToRange: action.payload }
    case 'SET_LEGAL_ENTITIES':
      return { ...state, selectedLegalEntityIds: action.payload }
    case 'SET_EMPLOYEES':
      return { ...state, selectedEmployeeIds: action.payload }
    case 'SET_PERIOD':
      return { ...state, period: action.payload }
    case 'SET_LOCATIONS':
      return { ...state, locationIds: action.payload }
    case 'SET_COST_CENTERS':
      return { ...state, costCenterIds: action.payload }
    case 'SET_CONCEPT_IDS':
      return { ...state, conceptIds: action.payload }
    case 'SET_POLICY_IDS':
      return { ...state, policyIds: action.payload }
    case 'SET_INSIGHT_TABLE_TYPE':
      return { ...state, insightTableType: action.payload }
    default:
      return state
  }
}

const maxRanges = {
  yearly: 2, // 2 years
  monthly: 12, // 12 months
  quarterly: 4, // 4 quarters
  weekly: 12, // 12 weeks
  daily: 31, // max 31 days (approx. 1 month)
  hourly: 1, // max 1 day
}

const adjustDateRange = (
  startDate: Date,
  endDate: Date,
  selectedFrequency: FinancialWorkspaceValueObjectsPeriodEnum
) => {
  const startMoment = moment(startDate)
  let endMoment = moment(endDate)

  const maxRange = maxRanges[selectedFrequency]

  let diffInUnits: number

  switch (selectedFrequency) {
    case 'yearly':
      diffInUnits = endMoment.diff(startMoment, 'years')
      if (diffInUnits > maxRange) {
        // Adjust end date to be within the max range of 2 years
        endMoment = startMoment.clone().add(maxRange, 'years')
      }
      break
    case 'monthly':
      diffInUnits = endMoment.diff(startMoment, 'months')
      if (diffInUnits > maxRange) {
        // Adjust end date to be within the max range of 12 months
        endMoment = startMoment.clone().add(maxRange, 'months')
      }
      break
    case 'quarterly':
      diffInUnits = endMoment.diff(startMoment, 'months') / 3
      if (diffInUnits > maxRange) {
        // Adjust end date to be within the max range of 4 quarters
        endMoment = startMoment.clone().add(maxRange * 3, 'months')
      }
      break
    case 'weekly':
      diffInUnits = endMoment.diff(startMoment, 'weeks')
      if (diffInUnits > maxRange) {
        // Adjust end date to be within the max range of 12 weeks
        endMoment = startMoment.clone().add(maxRange, 'weeks')
      }
      break
    case 'daily':
      diffInUnits = endMoment.diff(startMoment, 'days')
      if (diffInUnits > maxRange) {
        // Adjust end date to be within the max range of 31 days
        endMoment = startMoment.clone().add(maxRange, 'days')
      }
      break
    case 'hourly':
      diffInUnits = endMoment.diff(startMoment, 'hours')
      if (diffInUnits > maxRange * 24) {
        // Adjust end date to be within the max range of 1 day (24 hours)
        endMoment = startMoment.clone().add(1, 'days')
      }
      break
    default:
      break
  }

  // Return the adjusted start and end dates
  return [startMoment.toDate(), endMoment.toDate()]
}

// Date selector returns datetime, sending this to the backend for values like
// 2024-12-1T23:00:00.000Z would result in 2024-11-30, in the BE because of the timezone conversion
// It is better to format the date to the correct format before sending it to the backend
export const formatDate = (date: Date) => moment(date).format('YYYY-MM-DD')

const FinancialWorkspaceProviderContext = createContext<IValue | undefined>(undefined)

export const FinancialWorkspaceProvider: React.FC<Props> = ({
  children,
  insightTableType,
  defaultGroupBy,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { supportedCurrencies, loading: supportedCurrenciesLoading } = useSupportedCurrencies()
  const availabilities = useFinancialWorkspaceAvailabilities()
  const {
    period,
    policyIds,
    conceptIds,
    locationIds,
    costCenterIds,
    selectedRange,
    compareToRange,
    laading: availabilitiesLoading,
    selectedEmployeeIds,
    selectedLegalEntityIds,
  } = state

  const startDate = selectedRange[0]
  const endDate = selectedRange[1]

  const compareToStartDate = compareToRange?.[0]
  const compareToEndDate = compareToRange?.[1]

  // useMemo to adjust the date range when the period changes
  useUnsafeEffect(() => {
    const dates = adjustDateRange(selectedRange[0], selectedRange[1], period)
    if (dates[0] && dates[1]) {
      dispatch({ type: 'SET_RANGE', payload: [dates[0], dates[1]] })
    }
  }, [period, startDate.toISOString(), endDate.toISOString()]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch({ type: 'SET_INSIGHT_TABLE_TYPE', payload: insightTableType })
  }, [insightTableType])

  const formattedState = useMemo(
    () => ({
      period,
      insightTableType,
      policyIds: policyIds,
      conceptIds: conceptIds,
      groupBy: defaultGroupBy,
      includeFuturePeriods: false,
      locationIds: compact(locationIds),
      formattedEndDate: formatDate(endDate),
      costCenterIds: compact(costCenterIds),
      formattedStartDate: formatDate(startDate),
      employeeIds: compact(selectedEmployeeIds),
      legalEntityIds: compact(selectedLegalEntityIds),
      formattedCompareToEndDate: compareToEndDate ? formatDate(compareToEndDate) : '',
      formattedCompareToStartDate: compareToStartDate ? formatDate(compareToStartDate) : '',
    }),
    [
      period,
      endDate,
      startDate,
      policyIds,
      conceptIds,
      locationIds,
      costCenterIds,
      compareToEndDate,
      compareToStartDate,
      selectedEmployeeIds,
      selectedLegalEntityIds,
    ]
  )

  const hasComparisonDate = !!(
    formattedState.formattedCompareToEndDate && formattedState.formattedCompareToStartDate
  )

  if (availabilitiesLoading || supportedCurrenciesLoading) {
    return <LoadingWithSpinner />
  }

  return (
    <FinancialWorkspaceProviderContext.Provider
      value={{
        state,
        dispatch,
        formattedState,
        availabilities,
        insightTableType,
        hasComparisonDate,
        supportedCurrencies,
      }}
    >
      {children}
    </FinancialWorkspaceProviderContext.Provider>
  )
}

export const useFinancialWorkspace = () => {
  const context = useContext(FinancialWorkspaceProviderContext)
  if (context === undefined) {
    throw new Error('useFinancialWorkspace must be used within a FinancialWorkspaceProvider')
  }
  return context
}
